import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import '../styles/login.css';
import { getBranding } from '../config/branding';
import { useTranslation } from 'react-i18next';

const VerifyEmailPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage(t('auth.errNoToken'));
      return;
    }

    fetch(`${API_BASE_URL}/auth/verify-email?token=${encodeURIComponent(token)}`)
      .then(async res => {
        const data = await res.json();
        if (res.ok) {
          setStatus('success');
          setMessage(data.message || t('auth.verifiedOk'));
        } else {
          setStatus('error');
          setMessage(data.detail || t('auth.errVerification'));
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage(t('common.networkError'));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="login-page">
      <div className="login-bg">
        <div className="login-bg-gradient" />
        <div className="login-orb login-orb-1" />
        <div className="login-orb login-orb-2" />
        <div className="login-orb login-orb-3" />
      </div>

      <div className="login-container" style={{ maxWidth: 500 }}>
        <div className="login-form-container">
          <div className="login-form-card" style={{ maxWidth: 420 }}>
            {status === 'loading' && (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div className="login-spinner" style={{ margin: '0 auto 16px' }} />
                <p style={{ color: 'var(--text-secondary, #6c757d)', fontSize: 14 }}>
                  {t('auth.verifying')}
                </p>
              </div>
            )}

            {status === 'success' && (
              <div className="login-form-header">
                <div style={{ marginBottom: 16 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#2ecc71" strokeWidth="2" style={{ width: 48, height: 48 }}>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <h2>{t('auth.emailVerified')}</h2>
                <p style={{ marginTop: 12, lineHeight: 1.6 }}>
                  {t('auth.verifiedBody')}
                </p>
                <p style={{ marginTop: 8, lineHeight: 1.6, color: 'var(--text-secondary, #6c757d)', fontSize: 14 }}>
                  {t('auth.verifiedBody2')}
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="login-form-header">
                <div style={{ marginBottom: 16 }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="#e74c3c" strokeWidth="2" style={{ width: 48, height: 48 }}>
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                </div>
                <h2>{t('auth.verificationFailed')}</h2>
                <p style={{ marginTop: 12, lineHeight: 1.6, color: 'var(--text-secondary, #6c757d)' }}>
                  {message}
                </p>
                <Link
                  to="/login"
                  className="login-submit"
                  style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: 24 }}
                >
                  {t('auth.backToSignIn')}
                </Link>
              </div>
            )}
          </div>

          <p className="login-footer-text">
            {t('auth.copyright', { year: new Date().getFullYear(), brand: getBranding().brand_name })}
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
